import history from '../history';

import { buildDispatchable } from '../actions/_action-utilities';
import {
  featureSelection,
  getDetail,
  detailToggleArtifacts,
  setActiveProcess,
  featureActivation,
  importToAmplify,
  setActiveNote,
  applyCaseDetailReconciliation,
  dismissCaseDetailReconciliation,
  dismissCaseDetailFreshnessWarning
} from '../actions/cases.action';
import { caseEditsActions } from '../actions/case-edits';
import { openWindow, setBackToWorklistPath } from '../actions/ui';
import { codeBenchActions, appServiceLines } from '@codametrix/ui-common';

const { toServiceLineSlug, normalizeServiceLineName } = appServiceLines;

export const mapStateToProps = function(
  state: CMx.ApplicationState
): CMxCommonApp.CaseDetailData {
  const { cases, ui, caseEdits, capabilities, processInstance, accountSettings } = state;

  const demographicsPanelLocation = accountSettings?.accountDefaults?.formObject?.demographicsPanelLocation || 'right';
  const demographicsPanelVisibility = accountSettings?.accountDefaults?.formObject?.demographicsPanelVisibility || 'open';

  return {
    ...cases,
    processInstance,
    capabilities,
    propertyConfig: ui.propertyConfig,
    caseEdits: caseEdits.caseEdits,
    tenantId: ui.context.activeContext?.tenantId,
    hasAmplifyCaseRunV2Access: ui.hasAmplifyCaseRunV2Access,
    demographicsPanelLocation,
    demographicsPanelVisibility,
    caseAccessState: state.codeBench?.caseAccessState ?? '',
    caseDetailCache: cases.caseDetailCache ?? {
      pendingCaseDetail: null,
      showFreshnessWarning: false,
      lastAppliedDiff: null
    }
  };
};

export const mapDispatchToProps = (
  dispatch: any
): CMxCommonApp.CaseDetailMethods => {
  const runDispatchable = buildDispatchable(dispatch);

  return {
    onCaseAction: (action: any) => {
      runDispatchable(() => {
        return dispatch(importToAmplify(action));
      });
    },
    openChildWindow: (windowOpenArgs: CMxCommonApp.WindowOpenArguments) => {
      dispatch(openWindow(windowOpenArgs));
    },
    // Cases view → Amplify (not Worklist): use worklist URL for the bench but pass router state
    // that clears servicedesk cursor context so Next/prev worklist actions stay off (CDMX-15442).
    openAmplifyCase: (amplifyLaunchConfig: any) => {
      dispatch(setBackToWorklistPath({ path: 'worklist/' }));
      const sl = toServiceLineSlug(normalizeServiceLineName(String(amplifyLaunchConfig.serviceLine ?? '')));
      history.push(
        `/cmx/worklist/${sl}/case/${
          amplifyLaunchConfig.caseId
        }/case-run/${amplifyLaunchConfig.id}${
          amplifyLaunchConfig?.hasAmplifyCaseRunV2Access ? '/v2' : ''
        }/`,
        {
          servicedeskMode: false,
          worklistContext: {},
          workitemId: '',
          sessionId: amplifyLaunchConfig.sessionId ?? '',
          qaCollectionUuid: amplifyLaunchConfig.qaCollectionUuid ?? '',
          launchConfigServiceLine: amplifyLaunchConfig.serviceLine ?? ''
        }
      );
    },
    toggleArtifacts: (showAll: boolean) => {
      dispatch(detailToggleArtifacts(showAll));
    },
    onNavigate: (
      caseId: string,
      pushAction?: boolean,
      serviceLine?: string,
      tenantId?: string,
      hasAmplifyCaseRunV2Access?: boolean
    ) => {
      if (pushAction) {
        const sl = toServiceLineSlug(normalizeServiceLineName(String(serviceLine ?? '')));
        const targetPath = `/cmx/cases/${sl}/${caseId}/`;
        if (history.location.pathname.replace(/\/$/, '') !== targetPath.replace(/\/$/, '')) {
          history.push(targetPath);
        }
      }
      return dispatch(
        getDetail({
          caseId: caseId,
          serviceLine,
          tenantId,
          hasAmplifyCaseRunV2Access
        })
      );
    },
    onFeatureActivation: (selection: CMxCommonApp.ChargeSessionSelection) => {
      dispatch(featureActivation(selection));
    },
    onFeatureSelection: (selection: CMxArtifacts.FeatureSelection) => {
      dispatch(featureSelection(selection));
    },
    setActiveProcess: (activeProcessPayload: CMx.ActiveProcessPayload) => {
      dispatch(setActiveProcess(activeProcessPayload));
    },
    showList: (serviceLine: string, refreshList?: boolean) => {
      runDispatchable(() => {
        const slug = toServiceLineSlug(normalizeServiceLineName(serviceLine));
        if (refreshList) {
          history.replace(`/cmx/cases/${slug}/`);
        } else {
          history.replace(`/cmx/cases/${slug}/?back=true`);
        }
      });
    },
    updateValue: (updatedFieldAndValue: any) => {
      dispatch(caseEditsActions.updateValue(updatedFieldAndValue));
    },
    loadAllPatientDetailOptions: async (tenantId: string, facility: string) => {
      await dispatch(
        caseEditsActions.loadAllPatientDetailOptions({
          activeTenantId: tenantId,
          facility
        })
      );
    },
    updateCase: (updatesAndInfo: any) => {
      dispatch(caseEditsActions.updateCase(updatesAndInfo));
    },
    setActiveNote: (activeNote: CMxCommonApp.DecoratedNote) => {
      dispatch(setActiveNote(activeNote));
    },
    hideSideNavBar: () => {
      dispatch(codeBenchActions.hideSideNavBar());
    },
    showCoderNotesInfo: (coderNotesParams: CMxCommonApp.CoderNotesParams) => {
      dispatch(codeBenchActions.navigateCoderNotes(coderNotesParams));
    },
    showPatientInfo: caseRun => {
      dispatch(codeBenchActions.navigatePatient(caseRun));
    },
    applyCaseDetailReconciliation: () => {
      dispatch(applyCaseDetailReconciliation());
    },
    dismissCaseDetailReconciliation: () => {
      dispatch(dismissCaseDetailReconciliation());
    },
    dismissCaseDetailFreshnessWarning: () => {
      dispatch(dismissCaseDetailFreshnessWarning());
    }
  };
};
